import { useSearchParams } from 'react-router-dom';
import usePaginationLimit from './paginationLimitHook';
import { PaginationLimit, PaginationScreenKey } from '../services/paginationLimitService';

interface PaginationState {
  filter: string;
  page: number;
}

interface Pagination extends PaginationState {
  limit: number;
}

/** Filter and page in the URL, page size per screen; a new filter or size resets to page 1. */
export default function useUrlPagination(
  screen: PaginationScreenKey,
  defaultLimit?: PaginationLimit,
): [Pagination, (patch: Partial<Pagination>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = usePaginationLimit(screen, defaultLimit);

  const state: PaginationState = {
    filter: searchParams.get('filter') || '',
    page: +(searchParams.get('page') || 1),
  };

  function setPagination(patch: Partial<Pagination>): void {
    if (patch.limit !== undefined) {
      setLimit(patch.limit);
    }

    const renumbered = patch.filter !== undefined || patch.limit !== undefined;
    const filter = patch.filter ?? state.filter;
    const page = patch.page ?? (renumbered ? 1 : state.page);

    const params = new URLSearchParams(searchParams);

    if (filter) {
      params.set('filter', filter);
    } else {
      params.delete('filter');
    }

    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }

    setSearchParams(params, { replace: renumbered });
  }

  return [{ ...state, limit }, setPagination];
}
